// Wizard step 1 — onboarding fields (slice 4a-3).
//
// first_name + email are locked on (immutable, see OnboardingFieldsSchema).
// The optional fields each toggle enabled, and when enabled, a second toggle
// controls whether they're required at sign-up.
//
// "Show on pass" lives only in step 3 (FrontFieldsPicker) — surfacing it
// inline here too was redundant.

import { OPTIONAL_ONBOARDING_FIELDS } from '@naya/shared'
import type { OnboardingFields, OptionalOnboardingField } from '@naya/shared'

import type { BuilderController } from '../hooks/useBuilder.js'

const FIELD_LABELS: Record<OptionalOnboardingField, string> = {
  last_name: 'Last name',
  birthday: 'Birthday',
  phone: 'Phone number',
}

// Per-field explanatory copy under the label. Only fields that need a hint
// appear here — others fall back to no hint, keeping the rows compact.
const FIELD_HINTS: Partial<Record<OptionalOnboardingField, string>> = {
  birthday:
    'Month and day only, no year. Use this to send a birthday-month promotion (the "Birthday this month" broadcast segment).',
}

function LockedRow({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="program-field-row is-locked">
      <div className="program-field-main">
        <span className="program-field-label">{label}</span>
        <span className="program-field-hint">{hint}</span>
      </div>
      <span className="program-field-locked-pill">Required</span>
    </div>
  )
}

function OptionalRow({
  field,
  value,
  onChange,
}: {
  field: OptionalOnboardingField
  value: { enabled: boolean; required: boolean }
  onChange: (next: { enabled: boolean; required: boolean }) => void
}) {
  const hint = FIELD_HINTS[field]
  return (
    <div className="program-field-row">
      <div className="program-field-main">
        <label className="program-toggle">
          <input
            type="checkbox"
            checked={value.enabled}
            onChange={(e) =>
              onChange({
                enabled: e.target.checked,
                // Enabling defaults "required at sign-up" to true — the
                // common case for a field the owner cared enough to collect.
                // They can uncheck it explicitly. Disabling clears it.
                required: e.target.checked ? true : false,
              })
            }
          />
          <span className="program-field-label">{FIELD_LABELS[field]}</span>
        </label>
        {hint && <span className="program-field-hint">{hint}</span>}
      </div>
      {value.enabled && (
        <label className="program-toggle program-toggle-sub">
          <input
            type="checkbox"
            checked={value.required}
            onChange={(e) =>
              onChange({ enabled: true, required: e.target.checked })
            }
          />
          <span>Required at sign-up</span>
        </label>
      )}
    </div>
  )
}

export function StepOnboarding({ builder }: { builder: BuilderController }) {
  const fields = builder.draft.onboarding_fields

  function setOptional(
    key: OptionalOnboardingField,
    next: { enabled: boolean; required: boolean },
  ) {
    const onboarding_fields: OnboardingFields = {
      ...fields,
      [key]: next,
    }
    builder.update({ onboarding_fields })
  }

  return (
    <div className="program-step">
      <h2>What info do you want from customers when they join?</h2>
      <p className="program-step-sub">
        Keep it short — every extra field is one more reason someone abandons
        sign-up. You can choose which of these to display on the pass front in
        step 3.
      </p>

      <div className="program-field-list">
        <LockedRow
          label="First name"
          hint="Used for the pass greeting (e.g. “Hi Maria”). Not an ID, so look customers up by email or phone, not name."
        />
        <LockedRow
          label="Email"
          hint="Always collected. Used to find a customer and recover their stamps if they lose their phone."
        />
        {OPTIONAL_ONBOARDING_FIELDS.map((f) => (
          <OptionalRow
            key={f}
            field={f}
            value={fields[f]}
            onChange={(next) => setOptional(f, next)}
          />
        ))}
      </div>
    </div>
  )
}
